import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  type LinkProps,
  type NavLinkProps,
} from "react-router-dom";

import { ErrorPanel } from "@/components/common/ErrorPanel";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { isApiError } from "@/lib/api";
import { fetchTenants, tenantKeys, type Tenant } from "@/lib/api/tenants";
import { setCurrentTenant } from "@/lib/api/tenantScope";

/**
 * Which tenant the console is looking at.
 *
 * The tenant is in the URL — `/t/{slug}/…` — and NOWHERE else. Not in a
 * cookie, not in localStorage, not in a header the SPA remembers. A link
 * pasted into a ticket therefore names the tenant it was about, and two tabs
 * on two tenants cannot interfere.
 *
 * The provider RESOLVES the slug before it renders anything under it. That is
 * the whole point: rendering the console first and correcting it afterwards
 * would show one tenant's cached rows under another tenant's name for however
 * long the list took to arrive. So an unresolved slug renders a skeleton, an
 * unknown slug renders 404, and nothing fails open.
 */

interface TenantContextValue {
  /** The slug from the URL, already known to be servable. */
  slug: string;
  /**
   * The tenant record, or null when the multitenant module is off — the
   * /tenants list refuses while it is off, and the only servable slug is then
   * `default`. Null means "no record was readable", never "no such tenant".
   */
  tenant: Tenant | null;
}

const TenantContext = React.createContext<TenantContextValue | null>(null);

/** The slug the server treats as the default tenant. tenant.DefaultSlug in Go. */
export const DEFAULT_TENANT_SLUG = "default";

/**
 * The tenant in scope, or null on a platform page.
 *
 * Null is a real answer: /admin/users and /tenants belong to the deployment
 * and have no tenant at all.
 */
export function useTenant(): TenantContextValue | null {
  return React.useContext(TenantContext);
}

/** The slug in scope, or null on a platform page. */
export function useTenantSlug(): string | null {
  return React.useContext(TenantContext)?.slug ?? null;
}

/**
 * Resolve one in-app path against the tenant in scope.
 *
 * `useTenantPath("/posture")` → "/t/acme/posture". On a platform page, where
 * there is no tenant, the path is returned unchanged rather than guessed at.
 */
export function useTenantPath(to: string): string {
  const resolve = useTenantPathFn();
  return resolve(to);
}

/**
 * The same resolution as a function, for the many places that build several
 * paths in one render (a nav table, a tab bar, a table of rows). A hook cannot
 * be called inside a map; this can.
 */
export function useTenantPathFn(): (to: string) => string {
  const slug = useTenantSlug();
  return React.useCallback(
    (to: string) => (slug === null || !to.startsWith("/") ? to : `/t/${encodeURIComponent(slug)}${to}`),
    [slug],
  );
}

/** navigate(), with every absolute path resolved against the tenant in scope. */
export function useTenantNavigate(): (to: string, options?: { replace?: boolean }) => void {
  const navigate = useNavigate();
  const resolve = useTenantPathFn();
  return React.useCallback(
    (to: string, options?: { replace?: boolean }) => {
      void navigate(resolve(to), options);
    },
    [navigate, resolve],
  );
}

/** <Link>, tenant-resolved. */
export function TenantLink({ to, ...rest }: LinkProps & { to: string }): React.ReactElement {
  const resolve = useTenantPathFn();
  return <Link to={resolve(to)} {...rest} />;
}

/** <NavLink>, tenant-resolved. */
export function TenantNavLink({
  to,
  ...rest
}: NavLinkProps & { to: string }): React.ReactElement {
  const resolve = useTenantPathFn();
  return <NavLink to={resolve(to)} {...rest} />;
}

/** True when the /tenants list refused because the module is off. */
function isModuleDisabled(error: unknown): boolean {
  return isApiError(error) && error.code === "module_disabled";
}

export interface TenantProviderProps {
  children?: React.ReactNode;
}

/**
 * The route element for `t/:tenant`.
 *
 * Four outcomes, and each is a different statement:
 *
 *   resolving            skeleton — never the console under an unverified slug
 *   unknown slug         404 — never a silent fall back to the default tenant
 *   list unreadable      the error, with a retry — never an assumed tenant
 *   module off, default  the console, with no tenant record to show
 */
export function TenantProvider({ children }: TenantProviderProps): React.ReactElement {
  const params = useParams();
  const slug = params.tenant ?? "";

  const tenants = useQuery({
    queryKey: tenantKeys.list(),
    queryFn: fetchTenants,
    staleTime: 60_000,
    retry: false,
  });

  const moduleOff = isModuleDisabled(tenants.error);
  const record = (tenants.data ?? []).find((t) => t.slug === slug) ?? null;
  // With the module off there is no list to resolve against and the server
  // serves exactly one slug. Accepting it here is not failing open: any other
  // slug is refused below, and the API refuses it again on every request.
  const servable = record !== null || (moduleOff && slug === DEFAULT_TENANT_SLUG);

  // Set BEFORE the children render, not in an effect. An effect runs after the
  // first paint, by which time the children would already have issued their
  // queries against the previous tenant's slug.
  //
  // There is deliberately no unmount cleanup here. Under StrictMode an effect
  // is mounted, torn down and mounted again, and a cleanup that cleared the
  // slug would clear it on a provider that is still on screen. The platform
  // shell clears it instead, on the way in — which is the only place it
  // matters, because that is the only tree with no tenant.
  if (servable) setCurrentTenant(slug);

  if (tenants.isPending) {
    return <LoadingSkeleton variant="page" label="Opening the tenant" />;
  }
  if (tenants.isError && !moduleOff) {
    return (
      <div className="p-6">
        <ErrorPanel
          action="resolve the tenant in this address"
          error={tenants.error}
          onRetry={() => {
            void tenants.refetch();
          }}
        />
      </div>
    );
  }
  if (!servable) {
    return <UnknownTenant slug={slug} />;
  }

  return (
    <TenantContext.Provider value={{ slug, tenant: record }}>
      {record !== null && !record.enabled ? <DisabledTenantBanner tenant={record} /> : null}
      {children ?? <Outlet />}
    </TenantContext.Provider>
  );
}

/**
 * A slug that names no tenant this deployment holds.
 *
 * It says so, and it names the slug. It does NOT quietly redirect to the
 * default tenant: an operator who followed a link to /t/acme/posture and
 * landed on the default tenant's posture would read one tenant's findings
 * believing them to be another's, which is the worst failure this console has.
 */
function UnknownTenant({ slug }: { slug: string }): React.ReactElement {
  return (
    <main className="flex min-h-[70vh] items-center justify-center p-6">
      <div
        role="alert"
        data-testid="tenant-not-found"
        className="w-full max-w-lg rounded-lg border border-border bg-card p-6"
      >
        <p className="font-mono text-xs uppercase tracking-wide text-ink-3">404 — Not found</p>
        <h1 className="mt-2 text-xl font-semibold text-ink">
          This deployment has no tenant called <span className="font-mono">{slug}</span>
        </h1>
        <p className="mt-2 max-w-prose text-sm text-ink-2">
          Nothing was loaded. The address names a tenant that is not in this deployment's tenant
          list — check the link, or pick a tenant from the switcher.
        </p>
        <Link
          to="/"
          className="focus-ring mt-4 inline-block rounded text-sm text-brand underline underline-offset-2"
        >
          Back to the console
        </Link>
      </div>
    </main>
  );
}

/**
 * A disabled tenant still READS. It is not scanned and no scan can be started
 * for it, and saying so beats a page whose "Run scan" button answers 409.
 */
function DisabledTenantBanner({ tenant }: { tenant: Tenant }): React.ReactElement {
  return (
    <div
      role="status"
      data-testid="tenant-disabled-banner"
      className="border-b border-state-manual/40 bg-state-manual-bg px-4 py-2 text-sm text-state-manual"
    >
      <span className="font-medium">{tenant.display_name} is disabled.</span> Everything already
      collected is still readable. No scan runs for it and none can be started until it is
      enabled again on Manage tenants.
    </div>
  );
}

/**
 * The platform shell's counterpart: it clears the tenant so a platform page
 * cannot inherit the slug of whichever tenant was open last.
 *
 * Called during render, not in an effect, for the same reason the provider
 * sets it during render: by the time an effect ran, the platform page's own
 * queries would already have gone out under the previous tenant's prefix.
 */
export function clearTenantScope(): void {
  setCurrentTenant(null);
}

/** For tests: mount a subtree as though it were inside `/t/{slug}`. */
export function TenantTestProvider({
  slug = "acme",
  tenant = null,
  children,
}: {
  slug?: string;
  tenant?: Tenant | null;
  children: React.ReactNode;
}): React.ReactElement {
  return <TenantContext.Provider value={{ slug, tenant }}>{children}</TenantContext.Provider>;
}

/** The current location, rewritten onto another tenant. Used by the switcher. */
export function useSwitchTenantHref(): (slug: string) => string {
  const location = useLocation();
  return React.useCallback(
    (slug: string) => {
      const next = location.pathname.replace(
        /^\/t\/[^/]+/,
        `/t/${encodeURIComponent(slug)}`,
      );
      const path = next === location.pathname ? `/t/${encodeURIComponent(slug)}/overview` : next;
      return `${path}${location.search}`;
    },
    [location.pathname, location.search],
  );
}
